import { Box, keyframes } from '@mui/material';
import DesignSystem from '../../designSystem';
import { useT } from '../../i18n/useT';

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

function Bone({
  width,
  height,
  radius = 8,
  delayMs = 0,
}: {
  width: number | string;
  height: number | string;
  radius?: number;
  delayMs?: number;
}) {
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: `${radius}px`,
        backgroundImage: `linear-gradient(
          90deg,
          ${DesignSystem.colors.surfaceElevated} 0%,
          ${DesignSystem.colors.hoverStrong} 45%,
          ${DesignSystem.colors.surfaceElevated} 90%
        )`,
        backgroundSize: '200% 100%',
        animation: `${shimmer} 1.35s ease-in-out infinite`,
        animationDelay: `${delayMs}ms`,
      }}
    />
  );
}

function SkeletonCard({ index }: { index: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.15,
        minHeight: 88,
        bgcolor: DesignSystem.colors.surface,
        borderRadius: DesignSystem.borderRadius.xl,
        border: `1px solid ${DesignSystem.colors.border}`,
        animation: `${riseIn} 0.38s ${DesignSystem.motion.easing} both`,
        animationDelay: `${index * 70}ms`,
      }}
    >
      <Bone width={64} height={64} radius={32} delayMs={index * 50} />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
        <Bone width={`${72 - (index % 3) * 10}%`} height={15} delayMs={index * 50 + 40} />
        <Bone width={`${48 - (index % 2) * 8}%`} height={12} delayMs={index * 50 + 90} />
        <Bone width={78} height={10} delayMs={index * 50 + 140} />
      </Box>
      <Bone width={22} height={22} radius={6} delayMs={index * 50 + 70} />
    </Box>
  );
}

export default function FeedSkeleton({ count = 6 }: { count?: number }) {
  const t = useT();
  return (
    <Box
      role="status"
      aria-label={t('loadingFeedAria')}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} index={i} />
      ))}
    </Box>
  );
}
